import debounce from "debounce";
import WebsiteBehaviors from "../website-behaviors/index.js";
import {injectStyles} from "../utils/document.js";
import {waitUntil} from "../utils/async.js";

class McGrawHillConnect extends WebsiteBehaviors {
	identifier() {
		return "McGrawHillConnect";
	}

	// @ts-expect-error TS(7006) FIXME: Parameter 'location' implicitly has an 'any' type.
	static test(location) {
		// @ts-expect-error TS(2531) FIXME: Object is possibly 'null'.
		return !!(/\bplayer-ui\.mheducation\.com$/.exec(window.top.location.hostname));
	}

	shouldFrameTakeControl() {
		return (window.top !== window.self);
	}

	async init() {
		if ((this as any).initialized) {
			// When re-initializing after iframe is wiped, enableHighlighting() in content-script/main.js
			// will call this function. We don't want to re-init anything here, so we just return.
			return;
		}
		(this as any).initialized = true;

		super.init();

		// In the top frame we only inject styles (handled by super.init())
		if (window.top === window.self)
			return;

		const config = {
			attributes: true,
			childList:  true,
			subtree:    true
		};

		const reInit = async () => {
			if (document.querySelector(".aa-right-menu-btn"))
				return;

			const pnstyles = document.getElementById("pn-contentscript-styles");
			await (this as any).hooks.reInit();

			if (!pnstyles) {
				(this as any).hooks.injectStyles(); // re-inject standard content-script styles
				// re-inject standard content-script styles
				this._injectStyles(); // re-inject site-specific styles
			}
		};

		(this as any).lastPopupStatus = false;
		const observer2 = new MutationObserver(debounce((mutationsList, observer) => {
			// Check if we need to update rendered annotations
			const pnUrl = document.documentElement.getAttribute("data-pn-url");
			if (pnUrl !== (this as any).lastPnUrl) {
				// data-pn-url was updated by hashchange event in topFrameHandler(),
				// so now we need to update the annotations that are rendered.
				(this as any).lastPnUrl = pnUrl;
				(this as any).hooks.renderAnnotations();
			}

			// Clear the [hidden] mcgraw hill popup menu if our popup menu just closed.
			// If we don't do this, only the first selection a user makes will work properly.
			// After that, selections will disappear a moment after our popup appears.
			// Known issue: If you try to highlight things in succession too quickly,
			// you'll see the disappearing highlight be we trigger the mcgraw hill menu
			// to close too slowly and it takes your next highlight with it.
			const havePopup = !!document.querySelector(".aa-menu");
			if (havePopup !== (this as any).lastPopupStatus) {
				(this as any).lastPopupStatus = havePopup;
				if (!havePopup)
					this.clearForeignPopup();
			}

			// check if we need to add the whole UI to the iframe again
			reInit();
		}, 500));

		// Watch the document node in the iframe DOM.
		// Everything inside it is swapped out when the page/chapter changes.
		observer2.observe(document, config);

		// @ts-expect-error TS(2531) FIXME: Object is possibly 'null'.
		window.top.postMessage({type: "MCGH_INIT"}, "*");
	}

	/**
	 * Active-frame styles (for the iframe).
	 */
	styles() {
		// language=CSS
		const styles = `
			.aa-highlight {
				padding: 0px !important; /* combat span[class$="-highlight"] rule from the site */
			}
		`;

		return styles;
	}

	// @ts-expect-error TS(2416) FIXME: Property 'topFrameHandler' in type 'McGrawHillConn... Remove this comment to see the full error message
	async topFrameHandler() {
		// language=CSS
		const styles = `
			html.pn-mcgh-enabled .md-dialog-container {
				display: none;
			}
			html.pn-mcgh-enabled .md-dialog-backdrop {
				display: none;
			}
			html.pn-mcgh-enabled .md-panel-outer-wrapper {
				display: none;
			}
		`;
		injectStyles(window.document, styles);

		// Prevent hiding the "navigate to your last known location?" modal
		setTimeout(async () => {
			await waitUntil(() => !document.querySelector("md-dialog.last-location-dialog"), 300, 60000);
			document.documentElement.classList.add("pn-mcgh-enabled");
		}, 1000); // (give it time to show in the first place)

		window.addEventListener("message", async (evt) => {
			if (typeof evt.data.type !== "undefined"
				&& evt.data.type === "MCGH_CLOSE_MENU"
			) {
				setTimeout(() => {
					try {
						// Close their popup stuff so the javascript doesn't prevent the next highlight from sticking
						const evt = new MouseEvent("click", {
							view:       window,
							bubbles:    true,
							cancelable: true
						});

						// @ts-expect-error TS(2531) FIXME: Object is possibly 'null'.
						document.querySelector(".md-dialog-content .action-close").dispatchEvent(evt);
					} catch (e) {
						console.error("PN: Failed to close dialog", e);
					}
				}, 500);
			}
		});

		const updateUrl = () => {
			try {
				const doc = (document.getElementById("clo-iframe") as any).contentWindow.document.documentElement;
				const url = window.location.href.split("!")[0]; // remove any "scroll position" indicators from the URL
				doc.setAttribute("data-pn-url", url);
			} catch (e) {
				console.error("PN: Failed to set data-pn-url in clo-iframe", e);
			}
		};
		// We need to delay this because, on some books, the document element changes so if
		// we don't do it we end up adding data-pn-url to a soon-to-be-dead element.
		window.addEventListener("hashchange", () => setTimeout(updateUrl, 500));
		// Make sure to also update the URL on init.
		window.addEventListener("message", async function (evt) {
			if (typeof evt.data.type !== "undefined"
				&& evt.data.type === "MCGH_CLOSE_MENU"
			)
				setTimeout(updateUrl, 500);
		});

		return {skipPageData: true}; // don't set page data overrides-- we'll manage the page data ourselves
	}

	clearForeignPopup() {
		// See the message listener in topFrameHandler().
		const close = {
			type: "MCGH_CLOSE_MENU"
		};

		// @ts-expect-error TS(2531) FIXME: Object is possibly 'null'.
		window.top.postMessage(close, "*");
	}

	/**
	 * @param {function} processOrgProxy
	 * @return {string}
	 */
	// @ts-expect-error TS(7006) FIXME: Parameter 'processOrgProxy' implicitly has an 'any... Remove this comment to see the full error message
	getPageUrl(processOrgProxy) {
		// We do some magic here to handle the page url on our own.
		// (See how we return {skipPageData: true} from topFrameHandler.)
		// But there doesn't appear to be anywhere obvious to get the book title from,
		// so we just let the default code figure out the title to use for the pageData
		// (which turns out to be the name of the book section currently displayed).

		let url = document.documentElement.getAttribute("data-pn-url");
		if (url)
			url = document.documentElement.getAttribute("data-pn-url-final") ?? processOrgProxy(url);
		else
			url = processOrgProxy(window.location.href);

		return url;
	}
}

export default McGrawHillConnect;
